/**
 * Validated exact-DoAR handoff context.
 * Contract: docs/doar-core/DOARSPACE_UNIVERSAL_WORKSPACE_CONTRACT.md #7, #8.
 *
 * Converges the ObjectId-validation pattern already duplicated across
 * products/SpaceApp.jsx (CanonicalDoarRoute, AiAssetHandoff, ReturnToAiLink,
 * canonicalDoarId) into one shared helper instead of adding a fourth copy of
 * the same /^[a-f0-9]{24}$/i check.
 *
 * Query parameters are navigation context, not authorization - the server
 * remains the authority on ownership for every id parsed here. This module
 * never reads or produces bearer tokens/secrets; it only recognizes short
 * opaque ids and a small allow-listed set of origin/mode strings.
 */

const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

const ALLOWED_ORIGINS = new Set(["ai", "todoar", "marketplace", "space"]);
const ALLOWED_REQUESTED_MODES = new Set(["1D", "2D", "2.5D", "3D", "3.5D", "4D", "spatial", "document"]);
const ALLOWED_INTENTS = new Set(["ask", "edit", "publish", "ar"]);

export function isObjectId(value) {
  return typeof value === "string" && OBJECT_ID_RE.test(value);
}

function safeObjectId(value) {
  const str = value == null ? "" : String(value);
  return isObjectId(str) ? str : null;
}

function safeOrigin(value) {
  const str = value == null ? "" : String(value).toLowerCase();
  return ALLOWED_ORIGINS.has(str) ? str : null;
}

function safeIntent(value) {
  const str = value == null ? "" : String(value).toLowerCase();
  return ALLOWED_INTENTS.has(str) ? str : null;
}

function safeRequestedMode(value) {
  const str = value == null ? "" : String(value);
  if (ALLOWED_REQUESTED_MODES.has(str)) return str;
  const upper = str.toUpperCase();
  return ALLOWED_REQUESTED_MODES.has(upper) ? upper : null;
}

// A conservative allow-list for free-text-ish fields we still want bounded:
// short, url-safe-ish tokens only - never anything shaped like a JWT/bearer
// token (which would contain '.' segments and be far longer).
function safeShortToken(value, { maxLength = 64 } = {}) {
  const str = value == null ? "" : String(value);
  if (!str || str.length > maxLength) return null;
  if (!/^[A-Za-z0-9_-]+$/.test(str)) return null;
  return str;
}

/**
 * @param {URLSearchParams|Record<string,string>} params
 * @returns {{
 *   doarId: string|null,
 *   revisionId: string|null,
 *   assetId: string|null,
 *   selectedAssetId: string|null, // read-only continuation, NOT an activation request
 *   selectedClipId: string|null, // read-only selection hint; server/film must validate
 *   origin: string|null,
 *   intent: string|null,
 *   requestedMode: string|null,
 *   requestedEditor: string|null,
 *   threadId: string|null,
 *   taskId: string|null,
 *   hasHandoff: boolean,
 * }}
 */
export function parseHandoffContext(params) {
  const get = (key) => {
    if (!params) return null;
    if (typeof params.get === "function") return params.get(key);
    return params[key] ?? null;
  };

  const context = {
    doarId: safeObjectId(get("doar") ?? get("doarId") ?? get("object")),
    revisionId: safeObjectId(get("revision") ?? get("revisionId")),
    assetId: safeObjectId(get("asset") ?? get("assetId")),
    selectedAssetId: safeObjectId(get("selectedAsset")),
    selectedClipId: safeShortToken(get("clip") ?? get("selectedClip"), { maxLength: 96 }),
    origin: safeOrigin(get("from") ?? get("origin")),
    intent: safeIntent(get("intent")),
    requestedMode: safeRequestedMode(get("mode") ?? get("requestedMode")),
    requestedEditor: safeShortToken(get("editor") ?? get("requestedEditor")),
    threadId: safeObjectId(get("thread") ?? get("threadId")),
    taskId: safeObjectId(get("task") ?? get("taskId")),
  };

  return { ...context, hasHandoff: Boolean(context.doarId || context.assetId) };
}

/**
 * Strips handoff-only query params from a URLSearchParams instance in place
 * and returns the remaining query string - used after a handoff has been
 * consumed so it is not silently re-applied on the next render/navigation.
 *
 * NOTE: this also removes navigation PROVENANCE (from/origin, thread, task).
 * After a successful asset-backed workspace entry that is the wrong thing to
 * do, because "Back to TheDoAR.ai" still needs from=ai&thread=<id> - use
 * stripConsumedEntryParams() there instead.
 */
export function stripHandoffParams(params) {
  const clone = new URLSearchParams(params);
  ["doar", "doarId", "object", "revision", "revisionId", "asset", "assetId", "selectedAsset", "clip", "selectedClip", "from", "origin", "intent", "mode", "requestedMode", "editor", "requestedEditor", "thread", "threadId", "task", "taskId", "claim"].forEach((key) => clone.delete(key));
  return clone.toString();
}

// The fields that would actually RE-TRIGGER opening a representation. Only
// these are consumed once an asset-backed entry has succeeded.
//
// Canonical identity is deliberately NOT in this list. `doar`/`object` and
// `revision` are context, not operations: WorkspaceSession derives
// activeDoarId from handoff.doarId (falling back to the active Asset's own
// doArObjectId, which a legacy Asset legitimately may not have) and derives
// activeRevisionId from handoff.revisionId alone, since no other revision
// source is wired yet. Stripping them would blank the canonical identity of
// the very DoAR that was just opened.
const CONSUMED_ENTRY_PARAMS = Object.freeze([
  "asset", "assetId",
  "mode", "requestedMode",
  "editor", "requestedEditor",
  "claim",
]);

/**
 * Removes only the entry-triggering operation params, preserving canonical
 * identity (doar/object, revision), navigation provenance (from/origin,
 * thread/threadId, task/taskId) and every unrelated query param - so a
 * consumed handoff cannot silently re-run, while the opened DoAR keeps its
 * identity and "Back to TheDoAR.ai" keeps working.
 */
export function stripConsumedEntryParams(params) {
  const clone = new URLSearchParams(params);
  // The exact Asset was server-checked and activated before this helper runs.
  // Retire the *operation* without discarding the read-only selection needed
  // on refresh and by the return link to TheDoAR.ai. selectedAsset never
  // triggers adoption/activation in WorkspaceEntryCoordinator.
  const openedAssetId = safeObjectId(clone.get("asset") ?? clone.get("assetId"));
  if (openedAssetId) clone.set("selectedAsset", openedAssetId);
  CONSUMED_ENTRY_PARAMS.forEach((key) => clone.delete(key));
  return clone.toString();
}
